// HT-1 · 領域を指定して意味を教える（原本は変更しない。座標は0〜1で保存）
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Trash, Plus, ArrowsOutSimple, ArrowsInSimple } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { LEARN, authHeader, detailOf, Panel, Pill, Empty, ROLE_JA } from './shared';

const POLARITY = [['none', '例にしない'], ['positive', '正しい例'],
  ['negative', '誤りやすい例']];

export default function Annotate({ session, meta, onChanged }) {
  const [imgUrl, setImgUrl] = useState(null);
  const [isImage, setIsImage] = useState(false);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [previewError, setPreviewError] = useState(null);
  const [big, setBig] = useState(false);
  const [draft, setDraft] = useState(null);
  const [form, setForm] = useState({
    region_role: 'title_block', source_field: '', canonical_field_key: '',
    candidate_text: '', example_polarity: 'positive', explanation: '',
    excluded: false, unreadable: false,
  });
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);
  const startRef = useRef(null);

  // PDFでもプレビューを出す。サーバー側で該当ページをPNGにして返す
  // （原本は変更しない）。画像書類はそのまま1ページとして扱う。
  useEffect(() => {
    let revoked = null;
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(
          `${LEARN}/teaching/documents/${session.document_id}/page.png`,
          { headers: authHeader(), responseType: 'blob', params: { page, scale: 1.6 } });
        if (!alive) return;
        revoked = URL.createObjectURL(r.data);
        setImgUrl(revoked);
        setPreviewError(null);
        const total = Number(r.headers['x-page-count'] || 1);
        setPageCount(Number.isFinite(total) && total > 0 ? total : 1);
        // 座標の意味づけは元ファイルの形式で決める（ページ数では決めない）
        setIsImage((r.headers['x-source-format'] || 'image') !== 'pdf');
      } catch (e) {
        if (!alive) return;
        setImgUrl(null);
        setPreviewError(detailOf(e, 'プレビュー画像を取得できませんでした'));
      }
    })();
    return () => { alive = false; if (revoked) URL.revokeObjectURL(revoked); };
  }, [session.document_id, page]);

  const rel = (e) => {
    const b = boxRef.current.getBoundingClientRect();
    return { x: (e.clientX - b.left) / b.width, y: (e.clientY - b.top) / b.height };
  };
  const onDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    startRef.current = rel(e);
    setDraft(null);
  };
  const onMove = (e) => {
    if (!startRef.current) return;
    const p = rel(e); const s = startRef.current;
    setDraft({ x: Math.min(s.x, p.x), y: Math.min(s.y, p.y),
      w: Math.abs(p.x - s.x), h: Math.abs(p.y - s.y) });
  };
  const onUp = (e) => {
    if (startRef.current) {
      const p = rel(e); const s = startRef.current;
      setDraft({ x: Math.min(s.x, p.x), y: Math.min(s.y, p.y),
        w: Math.abs(p.x - s.x), h: Math.abs(p.y - s.y) });
    }
    startRef.current = null;
  };

  const save = useCallback(async () => {
    if (!draft || draft.w < 0.01 || draft.h < 0.01) {
      toast.error('プレビュー上で範囲を指定してください'); return;
    }
    setBusy(true);
    try {
      await axios.post(`${LEARN}/teaching/sessions/${session.teaching_session_id}/units`, {
        ...form, bounding_box: draft, page: isImage ? null : page,
        locator_type: isImage ? 'image_region' : 'pdf_region',
        source_format: isImage ? 'image' : 'pdf',
        canonical_field_key: form.canonical_field_key || null,
      }, { headers: authHeader() });
      toast.success('領域を保存しました（原本は変更していません）');
      setDraft(null);
      setForm((f) => ({ ...f, source_field: '', candidate_text: '',
        canonical_field_key: '', explanation: '' }));
      onChanged?.();
    } catch (e) { toast.error(detailOf(e, '領域を保存できませんでした')); } finally { setBusy(false); }
  }, [draft, form, isImage, page, session.teaching_session_id, onChanged]);

  const remove = async (unitId) => {
    try {
      await axios.delete(
        `${LEARN}/teaching/sessions/${session.teaching_session_id}/units/${unitId}`,
        { headers: authHeader() });
      toast.success('領域を削除しました');
      onChanged?.();
    } catch (e) { toast.error(detailOf(e, '削除できませんでした')); }
  };

  const units = session.selected_source_units || [];
  return (
    <Panel title="領域を指定して意味を教える" testId="teach-annotate"
      hint="プレビュー上で範囲を指定し、その場所が何かを選びます。元のファイルは変更しません。">
      <div className={big ? 'space-y-3' : 'grid grid-cols-1 gap-3 lg:grid-cols-[3fr,2fr]'}>
        <div>
          <div className="flex flex-wrap items-center gap-2 pb-2">
            {pageCount > 1 && (
              <>
                <label className="text-[11.5px] text-zinc-600" htmlFor="teach-annotate-page">ページ</label>
                <input id="teach-annotate-page" type="number" min={1} max={pageCount} value={page}
                  onChange={(e) => setPage(Math.min(pageCount,
                    Math.max(1, Number(e.target.value) || 1)))}
                  className="h-9 w-20 rounded-md border border-zinc-200 px-2 text-[12px]"
                  data-testid="teach-annotate-page" />
                <span className="text-[11px] text-zinc-500" data-testid="teach-annotate-pagecount">
                  / {pageCount} ページ
                </span>
              </>
            )}
            <Button type="button" variant="outline" size="sm" onClick={() => setBig((v) => !v)}
              data-testid="teach-annotate-zoom"
              className="ml-auto h-8 text-[11.5px]">
              {big ? <ArrowsInSimple size={14} className="mr-1" />
                : <ArrowsOutSimple size={14} className="mr-1" />}
              {big ? '標準の大きさ' : '大きく表示'}
            </Button>
          </div>
          {previewError && (
            <p className="pb-2 text-[11.5px] text-rose-700" data-testid="teach-annotate-preview-error">
              {previewError}（ページ枠の上でも範囲を指定できます）
            </p>
          )}
          <div ref={boxRef} onPointerDown={onDown} onPointerMove={onMove}
            onPointerUp={onUp} style={{ touchAction: 'none' }}
            className={`relative w-full cursor-crosshair overflow-hidden rounded-md border border-zinc-300 bg-zinc-50 ${
              big ? 'aspect-[1.414/1] max-h-[78vh]' : 'aspect-[1.414/1]'}`}
            role="application" aria-label="領域を指定するプレビュー"
            data-testid="teach-annotate-canvas">
            {imgUrl && <img src={imgUrl} alt="書類のプレビュー"
              className="pointer-events-none h-full w-full object-contain" />}
            {units.filter((u) => (u.locator?.page || null) === (isImage ? null : page))
              .map((u) => u.locator?.bounding_box && (
                <span key={u.unit_id}
                  className={`absolute border-2 ${u.example_polarity === 'negative'
                    ? 'border-rose-500 bg-rose-500/10' : 'border-teal-600 bg-teal-600/10'}`}
                  style={{ left: `${u.locator.bounding_box.x * 100}%`,
                    top: `${u.locator.bounding_box.y * 100}%`,
                    width: `${u.locator.bounding_box.w * 100}%`,
                    height: `${u.locator.bounding_box.h * 100}%` }}
                  data-testid={`teach-annotate-box-${u.unit_id}`}>
                  <span className="absolute -top-4 left-0 whitespace-nowrap rounded bg-white/90 px-1 text-[10px] text-zinc-700">
                    {u.region_role_label_ja}{u.source_field ? ` / ${u.source_field}` : ''}
                  </span>
                </span>
              ))}
            {draft && (
              <span className="absolute border-2 border-dashed border-teal-700 bg-teal-700/10"
                style={{ left: `${draft.x * 100}%`, top: `${draft.y * 100}%`,
                  width: `${draft.w * 100}%`, height: `${draft.h * 100}%` }}
                data-testid="teach-annotate-draft" />
            )}
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-[11.5px] text-zinc-600" htmlFor="teach-region-role">
            この場所は何ですか
          </label>
          <select id="teach-region-role" value={form.region_role}
            onChange={(e) => setForm((f) => ({ ...f, region_role: e.target.value }))}
            className="h-10 w-full rounded-md border border-zinc-200 px-2 text-[12.5px]"
            data-testid="teach-region-role">
            {(meta?.region_roles || []).map((r) => (
              <option key={r.key} value={r.key}>{r.label_ja}</option>
            ))}
          </select>
          <input value={form.source_field}
            onChange={(e) => setForm((f) => ({ ...f, source_field: e.target.value }))}
            placeholder="図面に印字された文字（例: DESIGNED）" aria-label="印字された文字"
            className="h-10 w-full rounded-md border border-zinc-200 px-2 text-[12.5px]"
            data-testid="teach-region-source-field" />
          <select value={form.canonical_field_key}
            onChange={(e) => setForm((f) => ({ ...f, canonical_field_key: e.target.value }))}
            aria-label="対応する項目"
            className="h-10 w-full rounded-md border border-zinc-200 px-2 text-[12.5px]"
            data-testid="teach-region-canonical">
            <option value="">— 対応する項目（任意）—</option>
            {Object.entries(ROLE_JA).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            <option value="material_raw">材質</option>
            <option value="drawing_number">図番</option>
            <option value="part_name">品名</option>
          </select>
          <input value={form.candidate_text}
            onChange={(e) => setForm((f) => ({ ...f, candidate_text: e.target.value }))}
            placeholder="そこに書かれている文字（例: 小山）" aria-label="書かれている文字"
            className="h-10 w-full rounded-md border border-zinc-200 px-2 text-[12.5px]"
            data-testid="teach-region-candidate" />
          <select value={form.example_polarity}
            onChange={(e) => setForm((f) => ({ ...f, example_polarity: e.target.value }))}
            aria-label="例の種類"
            className="h-10 w-full rounded-md border border-zinc-200 px-2 text-[12.5px]"
            data-testid="teach-region-polarity">
            {POLARITY.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <textarea value={form.explanation}
            onChange={(e) => setForm((f) => ({ ...f, explanation: e.target.value }))}
            placeholder="この場所の説明（例: 設計者の押印欄です）" aria-label="この場所の説明"
            className="h-16 w-full rounded-md border border-zinc-200 px-2 py-1 text-[12.5px]"
            data-testid="teach-region-explanation" />
          <div className="flex flex-wrap gap-3">
            <label className="flex min-h-[32px] items-center gap-1.5 text-[12px] text-zinc-700">
              <input type="checkbox" checked={form.excluded}
                onChange={(e) => setForm((f) => ({ ...f, excluded: e.target.checked }))}
                className="h-8 w-8" data-testid="teach-region-excluded" />読み取らない領域
            </label>
            <label className="flex min-h-[32px] items-center gap-1.5 text-[12px] text-zinc-700">
              <input type="checkbox" checked={form.unreadable}
                onChange={(e) => setForm((f) => ({ ...f, unreadable: e.target.checked }))}
                className="h-8 w-8" data-testid="teach-region-unreadable" />判読できない
            </label>
          </div>
          <Button className="h-10 w-full bg-teal-700 text-[12.5px] hover:bg-teal-800"
            onClick={save} disabled={busy} data-testid="teach-region-save">
            <Plus size={13} className="mr-1" />この領域を保存
          </Button>
        </div>
      </div>

      <div className="pt-3">
        {units.length === 0 ? (
          <Empty testId="teach-annotate-empty">
            指定した領域はまだありません。プレビュー上で範囲を選び、意味を教えてください。
          </Empty>
        ) : (
          <ul className="space-y-1.5" data-testid="teach-annotate-list">
            {units.map((u) => (
              <li key={u.unit_id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-zinc-200 px-3 py-2"
                tabIndex={0} data-testid={`teach-annotate-row-${u.unit_id}`}>
                <span className="min-w-0 text-[12.5px] text-zinc-800">
                  {u.region_role_label_ja}
                  {u.source_field ? ` / ${u.source_field}` : ''}
                  {u.candidate_text ? `：${u.candidate_text}` : ''}
                  <span className="ml-2">
                    <Pill tone={u.example_polarity === 'negative' ? 'rose'
                      : u.example_polarity === 'positive' ? 'teal' : 'zinc'}>
                      {u.example_polarity_label_ja}
                    </Pill>
                  </span>
                  {u.canonical_field_key && (
                    <span className="ml-1.5"><Pill>{ROLE_JA[u.canonical_field_key] || u.canonical_field_key}</Pill></span>
                  )}
                  {u.excluded && <span className="ml-1.5"><Pill tone="amber">読み取らない</Pill></span>}
                  {u.unreadable && <span className="ml-1.5"><Pill tone="amber">判読できない</Pill></span>}
                </span>
                <Button size="sm" variant="outline" className="h-9 text-[11px]"
                  onClick={() => remove(u.unit_id)}
                  data-testid={`teach-annotate-remove-${u.unit_id}`}>
                  <Trash size={11} className="mr-1" />削除
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}
